// @flow

import { useState } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";

import { useTranslation } from "react-i18next";

// Deleting and saving are handed in: while an exercise is still being created its answers
// only exist in the form, and once it exists they are written through the server.
type Props = {
  answers: any;
  deleteAnswer: (answerId: any) => void;
  saveAnswer: (answer: any, changes: { text: string; isCorrect: boolean }) => void;
};

export const AnswerList = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const [editedAnswerId, setEditedAnswerId] = useState<any>(null);
  const [draftText, setDraftText] = useState("");
  const [draftIsCorrect, setDraftIsCorrect] = useState(false);

  const startEditing = (answer: any) => {
    setEditedAnswerId(answer.id);
    setDraftText(answer.text);
    setDraftIsCorrect(answer.isCorrect);
  };

  const stopEditing = () => {
    setEditedAnswerId(null);
    setDraftText("");
    setDraftIsCorrect(false);
  };

  const saveAnswer = (answer: any) => {
    props.saveAnswer(answer, { text: draftText.trim(), isCorrect: draftIsCorrect });
    stopEditing();
  };

  const getEditedTableItem = (answer: any) => {
    return (
      <tr key={"Answer" + answer.id}>
        <th className="is-vcentered">
          <input
            className="input is-small"
            type="text"
            value={draftText}
            autoFocus
            onChange={(event) => setDraftText(event.target.value)}
          />
        </th>
        <th className="is-vcentered">
          <input
            type="checkbox"
            checked={draftIsCorrect}
            title={t("markAsCorrect")}
            aria-label={t("markAsCorrect")}
            onChange={() => setDraftIsCorrect(!draftIsCorrect)}
          />
        </th>
        <td className="is-vcentered">
          <button
            className="button is-small is-success"
            disabled={draftText.trim() === ""}
            onClick={() => saveAnswer(answer)}
          >
            {t("save")}
          </button>
        </td>
        <td className="is-vcentered">
          <button className="button is-small" onClick={() => stopEditing()}>
            {t("cancel")}
          </button>
        </td>
      </tr>
    );
  };

  const getAnswerTableItem = (answer: any) => {
    if (answer.id === editedAnswerId) {
      return getEditedTableItem(answer);
    }
    return (
      <tr key={"Answer" + answer.id}>
        <th className="is-vcentered">{answer.text}</th>
        <th className="is-vcentered">
          {answer.isCorrect ? (
            <FaCheck className="has-text-success" title={t("answerIsCorrect")} aria-label={t("answerIsCorrect")} />
          ) : (
            <FaTimes className="has-text-grey" title={t("answerIsWrong")} aria-label={t("answerIsWrong")} />
          )}
        </th>
        <td className="is-vcentered">
          <button
            className="button   is-primary"
            disabled={editedAnswerId !== null}
            onClick={() => startEditing(answer)}
          >
            {t("edit")}
          </button>
        </td>
        <td className="is-vcentered">
          <button
            className="button   is-danger"
            disabled={editedAnswerId !== null}
            onClick={() => props.deleteAnswer(answer.id)}
          >
            {t("delete")}
          </button>
        </td>
      </tr>
    );
  };

  const getAnswerTable = () => {
    return (
      <div className="table-container">
        <table className="table is-striped is-narrow is-hoverable is-fullwidth">
          <thead className="is-italic">
            <tr>
              <td>{t("answer")}</td>
              <td>{t("answerCorrectColumn")}</td>
              <td>{t("options")}</td>
              <td>{t("")}</td>
            </tr>
          </thead>
          <tbody>
            {props.answers.map((answer: any) => {
              return getAnswerTableItem(answer);
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const getEmptyMessage = () => {
    return <p className="is-size-7">{t("noAnswers")} </p>;
  };

  let renderAnswerTable = (
    <div className="card my-2">
      <div className="card-content">
        <div className="columns">
          <div className="column has-text-left">
            {props.answers.length > 0 && getAnswerTable()}
            {props.answers.length === 0 && getEmptyMessage()}
          </div>
        </div>
      </div>
    </div>
  );

  return <div>{renderAnswerTable}</div>;
};
